import {
  Checkbox,
  DropdownInput,
  Form,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  TypeOfObjectValues,
  validateWorkflowVariableWithRegex,
  WorkflowTriggerDueDate,
  WorkflowTriggerTimePeriod,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect, useState } from "react";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationTriggerDueDate.module.scss";

const cx = classNames.bind(styles);

type StartDueDateFromType = "creation" | "variable";

const timePeriodOptions: Array<{
  value: TypeOfObjectValues<typeof WorkflowTriggerTimePeriod>;
  label: string;
}> = [
  { value: "hours", label: "Hour(s)" },
  { value: "days", label: "Day(s)" },
  { value: "weeks", label: "Week(s)" },
  { value: "months", label: "Month(s)" },
];

const startVariableOptions: Array<{
  value: StartDueDateFromType;
  label: string;
}> = [
  { label: "From Creation", value: "creation" },
  { label: "From a Variable", value: "variable" },
];

const WorkflowConfigurationTriggerDueDate = () => {
  const {
    setTriggerDueDate,
    setTriggerDueDateIsValid,
    getTriggerDueDateFormValues,
  } = useWorkflowConfigurationFormStore();

  const storeValues = getTriggerDueDateFormValues();

  const hasDuration = !!storeValues?.duration;

  const [isDueDateEnabled, setIsDueDateEnabled] = useState(hasDuration);

  const form = useForm<
    WorkflowTriggerDueDate & { _start_from: StartDueDateFromType }
  >({
    values: storeValues,
  });

  const formValues = form.watch();

  // Update form state:
  useEffect(() => {
    setTriggerDueDateIsValid(isDueDateEnabled ? form.formState.isValid : true);
  }, [form.formState.isValid, isDueDateEnabled, setTriggerDueDateIsValid]);

  // Conditionally register start_variable field based on due date type:
  useEffect(() => {
    if (formValues._start_from === "creation") {
      form.unregister("start_variable");
      handleValueChange("start_variable", undefined);
    } else {
      form.register("start_variable");
      handleValueChange("start_variable", storeValues?.start_variable);
    }
  }, [formValues._start_from]);

  const handleValueChange = (
    key: "duration" | "time_period" | "start_variable",
    value: unknown
  ) => {
    setTriggerDueDate({
      ...(storeValues ?? { duration: 0, time_period: "hours" }),
      [key]: value,
    });
  };

  return (
    <>
      <Checkbox
        name=""
        value={isDueDateEnabled}
        onChange={(isEnabled: boolean) => {
          setIsDueDateEnabled(isEnabled);
          if (!isEnabled) {
            handleValueChange("duration", 0);
          }
        }}
        label={"Define workflow due date"}
      />
      {isDueDateEnabled && (
        <Form {...form}>
          <div className={cx("linear-container")}>
            <TextInput
              name="duration"
              type="number"
              isRequired
              placeholder="Duration"
              className={cx("input")}
              min={0}
              step={1}
              onChange={(value: string) => {
                handleValueChange("duration", Number(value));
              }}
            />
            <DropdownInput
              name="time_period"
              options={timePeriodOptions}
              labelKey="label"
              valueKey="value"
              isRequired
              placeholder="Time period"
              className={cx("input")}
              isSearchable={false}
              isClearable={false}
              onChange={(value: string) => {
                handleValueChange("time_period", value);
              }}
            />

            <DropdownInput
              className={cx("input")}
              name="_start_from"
              options={startVariableOptions}
              labelKey="label"
              valueKey="value"
              isSearchable={false}
              isClearable={false}
            />
          </div>
          {formValues._start_from === "variable" && (
            <TextInput
              label="Variable to Start From"
              description={`When this variable is set, workflow due date calculation will start from this variable.`}
              name="start_variable"
              isRequired
              rules={{
                required: {
                  value: true,
                  message: "Variable is required",
                },
                validate: (value: string) => {
                  if (!validateWorkflowVariableWithRegex(value))
                    return "Invalid variable format";
                  return true;
                },
              }}
              onChange={(value: string) => {
                handleValueChange("start_variable", value);
              }}
            />
          )}
        </Form>
      )}
    </>
  );
};

export default WorkflowConfigurationTriggerDueDate;
